import {
  initializeGoogleAnalytics,
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
} from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import { trackEvent, trackOwnedAnalyticsOnly, trackScreenView } from "@/lib/analytics-events";

export {
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
  initializeGoogleAnalytics,
  trackEvent,
  trackOwnedAnalyticsOnly,
  trackScreenView,
};

declare global {
  interface Window {
    __ishvaramBootstrapDistinctId?: string;
  }
}

/**
 * The owned browser identity lives in the leaf module ./analytics-identity so
 * the beacon path and checkout API helpers can share one browser-scoped ID
 * without a circular import. Re-export aliases keep existing callers stable.
 */
export { getOrCreateBootstrapDistinctId };
export const getOrCreateAnalyticsDistinctId = getOrCreateBootstrapDistinctId;
export const getAnalyticsDistinctId = getOrCreateBootstrapDistinctId;

/** Identify user across all analytics providers */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (typeof window === "undefined") return;

  // GA4: set user_id so all subsequent events carry the authenticated
  // identifier. Without this, GA4 reports 100% of users as anonymous —
  // makes auth-funnel analysis impossible (verified 2026-05-24 via
  // v_ga4_auth_funnel_daily). gtag.set persists across pageviews.
  try {
    if (typeof window.gtag === "function") {
      window.gtag("set", { user_id: userId });
    }
  } catch {
    // gtag may not be loaded yet — that's fine; the next event with
    // user_id in props is enough to backfill once gtag boots.
  }

  sendOwnedAnalyticsEvent("user_identified", { user_id: userId, ...(traits ?? {}) });
}

/** Reset user identity (on logout) */
export function resetUser() {
  if (typeof window === "undefined") return;
  sendOwnedAnalyticsEvent("user_reset", {});
}

// ── Typed event helpers ──

export function trackCalculatorUsed(props: {
  calculator: string;
  language?: string;
  [key: string]: unknown;
}) {
  trackEvent("calculator_used", props);
  trackFreeToolCompleted({
    tool_name: props.calculator,
    language: props.language,
    source_path: typeof props.source_path === "string" ? props.source_path : undefined,
  });
}

export function trackMatchingCompleted(props: { score: number; language?: string }) {
  trackEvent("matching_completed", props);
}

export function trackFreeToolStarted(props: {
  tool_name: string;
  source_path?: string;
  language?: string;
  placement?: string;
  step?: string;
}) {
  trackEvent("free_tool_started", props);
}

export function trackFreeToolCompleted(props: {
  tool_name: string;
  source_path?: string;
  language?: string;
  placement?: string;
  step?: string;
  has_time?: boolean;
}) {
  trackEvent("free_tool_completed", props);
}

export function trackSignUp(props: { method: string }) {
  trackEvent("sign_up", props);
}

export function trackRashifalViewed(props: { sign: string; period: string }) {
  trackEvent("rashifal_viewed", props);
}

export function trackSEOPageViewed(props: {
  source_path: string;
  canonical_url?: string;
  referrer?: string;
  intent: string;
  page_category: string;
  purchase_intent_score?: number;
  purchase_intent_band?: string;
  purchase_intent_reason?: string;
}) {
  trackEvent("seo_page_viewed", props);
}

export * from "@/lib/analytics-checkout";
export * from "@/lib/analytics-chat";
export * from "@/lib/analytics-kundali";
export * from "@/lib/analytics-report";
